import { useCallback, useEffect, useState, type FormEvent } from "react";
import { ArrowDownLeft, Banknote, Calculator, LockKeyhole, Plus, WalletCards } from "lucide-react";
import { Button, EmptyState, FieldLabel, InlineAlert, LoadingState, MetricCard, Page, PageHeader, Panel, TextField } from "../../design-system/react";
import { calculateCashDifference, calculateCashSummary } from "../domain/cash";
import { mxn } from "../domain/money";
import type { CashMovement, CashSession } from "../domain/types";
import { Modal } from "../components/Modal";
import { mapCashMovement, mapCashSession } from "../lib/cashSessions";
import { supabase } from "../lib/supabase";
import { useApp } from "../state/AppContext";

function rpcErrorMessage(error: { message?: string } | null) {
  if (!error) return "Ocurrió un error inesperado.";
  if (error.message?.includes("Note required")) return "El motivo o insumo es obligatorio.";
  if (error.message?.includes("Amount must be greater than zero")) return "El importe debe ser mayor que cero.";
  if (error.message?.includes("Cash session not open")) return "Este turno ya no está abierto.";
  if (error.message?.includes("Invalid opening fund")) return "El fondo inicial no es válido.";
  if (error.message?.includes("Invalid counted amount")) return "El efectivo contado no es válido.";
  if (error.message?.includes("Idempotency key already used")) return "Este movimiento ya fue registrado.";
  if (error.message?.includes("one_open_cash_session")) return "Ya hay un turno abierto.";
  // Jerga de Postgres que llegaba tal cual a la pantalla: le pasa a una sesión cuyo acceso al
  // servidor caducó, así que lo accionable es volver a entrar, no el nombre de la función.
  if (error.message?.includes("permission denied")) return "Tu sesión expiró. Vuelve a entrar para abrir el turno.";
  return error.message ?? "Ocurrió un error inesperado.";
}

function OpenSessionModal({ onClose, onOpen }: { onClose: () => void; onOpen: (fund: number) => Promise<string | null> }) {
  const [fund, setFund] = useState("500");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setLoading(true);
    const result = await onOpen(Number(fund));
    setLoading(false);
    if (result) setError(result);
  }

  return (
    <Modal title="Abrir turno" description="Registra el fondo inicial con el que arrancas la caja." onClose={onClose}>
      {error && <div className="mb-4"><InlineAlert>{error}</InlineAlert></div>}
      <form className="space-y-4" onSubmit={submit}>
        <FieldLabel label="Fondo inicial">
          <TextField type="number" min="0" value={fund} onChange={(event) => setFund(event.target.value)} autoFocus required />
        </FieldLabel>
        <Button type="submit" variant="primary" className="w-full" disabled={loading || !Number(fund) && fund !== "0"}>
          {loading ? "Abriendo…" : "Abrir turno"}
        </Button>
      </form>
    </Modal>
  );
}

function WithdrawModal({ onClose, onSubmit }: { onClose: () => void; onSubmit: (amount: number, note: string) => Promise<string | null> }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit() {
    setError("");
    setLoading(true);
    const result = await onSubmit(Number(amount), note.trim());
    setLoading(false);
    if (result) setError(result);
  }

  return (
    <Modal title="Retiro parcial" description="El importe y la nota son obligatorios." onClose={onClose}>
      <div className="space-y-4">
        {error && <InlineAlert>{error}</InlineAlert>}
        <FieldLabel label="Importe">
          <TextField type="number" min="1" value={amount} onChange={(event) => setAmount(event.target.value)} />
        </FieldLabel>
        <FieldLabel label="Motivo o insumo">
          <TextField value={note} onChange={(event) => setNote(event.target.value)} placeholder="Ej. compra de hielo" />
        </FieldLabel>
        <Button variant="primary" className="w-full" disabled={loading || !Number(amount) || !note.trim()} onClick={submit}>
          <Plus size={18} /> {loading ? "Registrando…" : "Registrar retiro"}
        </Button>
      </div>
    </Modal>
  );
}

function CloseSessionModal({ expected, onClose, onSubmit }: { expected: number; onClose: () => void; onSubmit: (counted: number) => Promise<CashSession | string> }) {
  const [counted, setCounted] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<CashSession | null>(null);

  async function submit() {
    setError("");
    setLoading(true);
    const outcome = await onSubmit(Number(counted));
    setLoading(false);
    if (typeof outcome === "string") setError(outcome);
    else setResult(outcome);
  }

  if (result) {
    const { difference, status } = calculateCashDifference(result.countedCash ?? 0, result.expectedCash ?? 0);
    return (
      <Modal title="Corte realizado" onClose={onClose}>
        <div className="space-y-4">
          <InlineAlert tone={status === "exact" ? "success" : "error"}>
            {status === "exact"
              ? "El conteo coincide exactamente con lo esperado."
              : status === "surplus"
                ? `Sobrante de ${mxn.format(difference)} respecto a lo esperado.`
                : `Faltante de ${mxn.format(Math.abs(difference))} respecto a lo esperado.`}
          </InlineAlert>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-on-surface-variant">Esperado</span><strong>{mxn.format(result.expectedCash ?? 0)}</strong></div>
            <div className="flex justify-between"><span className="text-on-surface-variant">Contado</span><strong>{mxn.format(result.countedCash ?? 0)}</strong></div>
          </div>
          <Button variant="primary" className="w-full" onClick={onClose}>Cerrar</Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Hacer corte" description="Cuenta el efectivo físico en caja e ingresa el total." onClose={onClose}>
      <div className="space-y-4">
        {error && <InlineAlert>{error}</InlineAlert>}
        <p className="text-sm text-on-surface-variant">Esperado antes del conteo: <strong className="text-on-surface">{mxn.format(expected)}</strong></p>
        <FieldLabel label="Efectivo contado">
          <TextField type="number" min="0" value={counted} onChange={(event) => setCounted(event.target.value)} autoFocus />
        </FieldLabel>
        <Button variant="primary" className="w-full" disabled={loading || (!Number(counted) && counted !== "0")} onClick={submit}>
          <LockKeyhole size={18} /> {loading ? "Cerrando…" : "Confirmar corte"}
        </Button>
      </div>
    </Modal>
  );
}

export function CashPage() {
  const { session, cashSessionRequired, refreshCashSession } = useApp();
  const [cashSession, setCashSession] = useState<CashSession | null>(null);
  const [movements, setMovements] = useState<CashMovement[]>([]);
  const [cashSalesCents, setCashSalesCents] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openModalOpen, setOpenModalOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [closeModalOpen, setCloseModalOpen] = useState(false);

  const load = useCallback(async () => {
    if (!supabase) { setLoading(false); return; }
    setLoading(true);
    setError("");
    const { data: sessionRow, error: sessionError } = await supabase.from("cash_sessions").select("*").is("closed_at", null).maybeSingle();
    if (sessionError) { setError(sessionError.message); setLoading(false); return; }
    if (!sessionRow) { setCashSession(null); setMovements([]); setCashSalesCents(0); setLoading(false); void refreshCashSession(); return; }
    const session = mapCashSession(sessionRow);
    const [{ data: movementRows, error: movementError }, { data: paymentRows, error: paymentError }] = await Promise.all([
      supabase.from("cash_movements").select("*").eq("cash_session_id", session.id).order("created_at", { ascending: false }),
      // Una venta revertida o cancelada conserva sus pagos, así que hay que descontarla del
      // efectivo esperado; si no, el corte reporta un faltante por dinero que ya se devolvió.
      supabase.from("payments").select("amount_cents, orders!inner(status)").eq("method", "cash").gte("created_at", session.openedAt).not("orders.status", "in", "(reversed,cancelled)")
    ]);
    if (movementError) setError(movementError.message);
    else if (paymentError) setError(paymentError.message);
    setCashSession(session);
    setMovements((movementRows ?? []).map(mapCashMovement));
    setCashSalesCents((paymentRows ?? []).reduce((sum, row) => sum + Number(row.amount_cents), 0));
    setLoading(false);
    // El candado de pedidos vive en el contexto: hay que moverlo con cada apertura y cada corte.
    void refreshCashSession();
  }, [refreshCashSession]);

  useEffect(() => { void load(); }, [load]);

  const openSession = useCallback(async (fund: number) => {
    if (!supabase) return "Caja no disponible sin conexión a Supabase.";
    if (!navigator.onLine) return "Necesitas conexión a internet para abrir un turno.";
    const { error: rpcError } = await supabase.rpc("open_cash_session", { p_opening_fund_cents: Math.round(fund * 100) });
    if (rpcError) return rpcErrorMessage(rpcError);
    setOpenModalOpen(false);
    await load();
    return null;
  }, [load]);

  const recordWithdrawal = useCallback(async (amount: number, note: string) => {
    if (!supabase || !cashSession) return "Caja no disponible sin conexión a Supabase.";
    if (!navigator.onLine) return "Necesitas conexión a internet para registrar un retiro.";
    const { error: rpcError } = await supabase.rpc("record_cash_movement", {
      p_cash_session_id: cashSession.id, p_type: "withdrawal", p_amount_cents: Math.round(amount * 100), p_note: note, p_idempotency_key: crypto.randomUUID()
    });
    if (rpcError) return rpcErrorMessage(rpcError);
    setWithdrawOpen(false);
    await load();
    return null;
  }, [cashSession, load]);

  const closeSession = useCallback(async (counted: number): Promise<CashSession | string> => {
    if (!supabase || !cashSession) return "Caja no disponible sin conexión a Supabase.";
    if (!navigator.onLine) return "Necesitas conexión a internet para hacer el corte.";
    const { data, error: rpcError } = await supabase.rpc("close_cash_session", { p_cash_session_id: cashSession.id, p_counted_cash_cents: Math.round(counted * 100) });
    if (rpcError || !data) return rpcErrorMessage(rpcError);
    return mapCashSession(data as Record<string, unknown>);
  }, [cashSession]);

  if (!supabase) {
    return <Page size="wide"><EmptyState icon={<WalletCards />} title="Caja no disponible" description="Configura Supabase para operar el arqueo de caja." /></Page>;
  }

  if (loading) {
    return <Page size="wide"><LoadingState label="Cargando turno de caja…" /></Page>;
  }

  if (!cashSession) {
    return (
      <Page size="wide">
        <PageHeader eyebrow="CONTROL DE EFECTIVO" title="Caja" description="No hay un turno abierto." />
        {error && <div className="mb-4"><InlineAlert>{error}</InlineAlert></div>}
        {cashSessionRequired && <div className="mb-4"><InlineAlert>Mientras la caja esté cerrada no se pueden tomar pedidos nuevos. Abre el turno para reanudar el servicio.</InlineAlert></div>}
        <EmptyState
          icon={<WalletCards />}
          title="Sin turno abierto"
          description="Abre un turno con tu fondo inicial para empezar a registrar movimientos de caja y poder tomar pedidos."
          action={<Button variant="primary" onClick={() => setOpenModalOpen(true)}><WalletCards size={18} /> Abrir turno</Button>}
        />
        {openModalOpen && <OpenSessionModal onClose={() => setOpenModalOpen(false)} onOpen={openSession} />}
      </Page>
    );
  }

  const cash = cashSalesCents / 100;
  const { withdrawals: withdrawn, withdrawalCount, expected } = calculateCashSummary({
    openingFund: cashSession.openingFund,
    cashSales: cash,
    movements
  });
  const withdrawalMovements = movements.filter((movement) => movement.type === "withdrawal" || movement.type === "adjustment");
  const openedByMe = cashSession.openedBy === session?.id;

  return (
    <Page size="wide">
      <PageHeader
        eyebrow="CONTROL DE EFECTIVO"
        title="Caja"
        description={`Turno abierto por ${openedByMe ? "ti" : "otro miembro del equipo"} · ${new Date(cashSession.openedAt).toLocaleString("es-MX", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })}`}
        action={<><Button onClick={() => setWithdrawOpen(true)}><ArrowDownLeft size={18} /> Registrar retiro</Button><Button variant="primary" onClick={() => setCloseModalOpen(true)}><LockKeyhole size={18} /> Hacer corte</Button></>}
      />
      {error && <div className="mb-4"><InlineAlert>{error}</InlineAlert></div>}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard icon={<WalletCards />} label="Fondo inicial" value={mxn.format(cashSession.openingFund)} tone="primary" />
        <MetricCard icon={<Banknote />} label="Efectivo en ventas" value={mxn.format(cash)} detail="Desde la apertura del turno" tone="success" />
        <MetricCard icon={<ArrowDownLeft />} label="Retiros" value={mxn.format(withdrawn)} detail={`${withdrawalCount} movimientos`} tone="danger" />
        <MetricCard icon={<Calculator />} label="Efectivo esperado" value={mxn.format(expected)} detail="Efectivo físico de este turno" />
      </div>
      <p className="mt-2 text-xs text-on-surface-variant">Una reversión de una venta cobrada en un turno anterior no se descuenta aquí, aunque sí aparezca en Reportes del día.</p>
      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_380px]">
        <Panel className="p-5">
          <h2 className="text-lg font-bold">Movimientos del turno</h2>
          {withdrawalMovements.length ? (
            <div className="mt-4 divide-y divide-outline-variant/25">
              {withdrawalMovements.map((movement) => (
                <div className="flex items-center justify-between py-4" key={movement.id}>
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-error-container text-error"><ArrowDownLeft size={18} /></span>
                    <div>
                      <p className="font-semibold">Retiro de efectivo</p>
                      <p className="text-xs text-on-surface-variant">{new Date(movement.createdAt).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })} · {movement.note}</p>
                    </div>
                  </div>
                  <span className="font-bold text-error">-{mxn.format(movement.amount)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-4 text-sm text-on-surface-variant">Sin movimientos todavía.</p>
          )}
        </Panel>
        <Panel className="bg-primary p-6 text-on-primary">
          <p className="text-xs font-bold uppercase tracking-wider text-on-primary/65">Resumen del arqueo</p>
          <div className="mt-5 space-y-3 text-sm">
            <div className="flex justify-between"><span>Fondo inicial</span><strong>{mxn.format(cashSession.openingFund)}</strong></div>
            <div className="flex justify-between"><span>+ Ventas efectivo</span><strong>{mxn.format(cash)}</strong></div>
            <div className="flex justify-between"><span>- Retiros</span><strong>{mxn.format(withdrawn)}</strong></div>
            <div className="border-t border-on-primary/20 pt-4">
              <div className="flex items-end justify-between"><span className="font-bold">Esperado</span><strong className="text-3xl">{mxn.format(expected)}</strong></div>
            </div>
          </div>
          <p className="mt-6 text-xs leading-5 text-on-primary/65">El conteo real y la diferencia se registran al hacer el corte. El historial queda bloqueado después del cierre.</p>
        </Panel>
      </div>
      {withdrawOpen && <WithdrawModal onClose={() => setWithdrawOpen(false)} onSubmit={recordWithdrawal} />}
      {closeModalOpen && (
        <CloseSessionModal
          expected={expected}
          onClose={() => {
            setCloseModalOpen(false);
            void load();
          }}
          onSubmit={closeSession}
        />
      )}
    </Page>
  );
}
